// WhatIsATransientLearning.jsx
// Route suggestion: /learning/what-is-a-transient

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Chip,
  Button,
  useMediaQuery,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import CompressRoundedIcon from "@mui/icons-material/CompressRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import WavesRoundedIcon from "@mui/icons-material/WavesRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";
import ArticleSchemaMarkup from "./ArticleSchemaMarkup.jsx";

export default function WhatIsATransientLearning() {
  const { style } = useStyle();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";

  const quickFacts = [
    {
      title: "What a transient is",
      text: "A transient is the short burst of energy at the beginning of a sound, often responsible for attack, punch, click, snap, or impact.",
      icon: <BoltRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "Transients help your ear identify timing, rhythm, impact, and separation. They are a big part of why drums hit, guitars pick, and vocals feel articulate.",
      icon: <HeadphonesRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Best first move",
      text: "Before processing transients, decide whether the sound needs more attack, less sharpness, more sustain, or better balance in the mix.",
      icon: <TuneRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const partsOfSound = [
    {
      title: "Attack",
      description:
        "The very beginning of a sound. This is where the transient usually lives. It can include the stick hit on a snare, the pick on a guitar string, or the consonant at the front of a vocal phrase.",
    },
    {
      title: "Body",
      description:
        "The main tonal part of the sound after the initial attack. The body gives the sound weight, note identity, fullness, and size.",
    },
    {
      title: "Sustain",
      description:
        "The portion that continues after the attack. Sustain affects how long a drum rings, how long a guitar note holds, or how much room tone stays audible.",
    },
    {
      title: "Decay",
      description:
        "The way the sound fades out over time. Decay can be short and tight, long and roomy, smooth, noisy, or resonant.",
    },
  ];

  const transientExamples = [
    {
      title: "Kick drum",
      description:
        "The transient may sound like click, beater impact, or punch. The body carries the low-end weight.",
    },
    {
      title: "Snare drum",
      description:
        "The transient gives the snare crack and snap. The sustain includes ring, shell tone, wires, and room sound.",
    },
    {
      title: "Acoustic guitar",
      description:
        "The transient comes from the pick or finger hitting the string. Too much can sound scratchy; too little can sound dull.",
    },
    {
      title: "Bass guitar",
      description:
        "The transient helps bass notes speak clearly. Without it, bass can feel slow or blurred, even if the low end is strong.",
    },
    {
      title: "Vocals",
      description:
        "Vocals have small transient-like details in consonants such as T, K, P, S, and F. These details help words remain intelligible.",
    },
    {
      title: "Piano",
      description:
        "The hammer attack creates a clear transient, while the strings provide the body and sustain.",
    },
  ];

  const transientControls = [
    {
      title: "Transient shaper",
      description:
        "A transient shaper lets you adjust attack and sustain directly. It can make drums punchier, tighten room tone, or soften sharp hits without traditional compression settings.",
    },
    {
      title: "Compression",
      description:
        "A compressor can reduce or emphasize transients depending on attack and release settings. Slow attack often lets more transient through; fast attack can clamp down on it.",
    },
    {
      title: "Clipping",
      description:
        "Clipping can trim sharp peaks from transients, making a track louder or more controlled. Used too hard, it can flatten impact or add harshness.",
    },
    {
      title: "EQ",
      description:
        "EQ does not directly change the shape of a transient, but it can emphasize or reduce the frequencies that make attack feel clicky, sharp, dull, or punchy.",
    },
    {
      title: "Saturation",
      description:
        "Saturation can round peaks, add harmonics, and make transients feel denser or more energetic. It can also soften the initial edge if pushed.",
    },
    {
      title: "Editing and timing",
      description:
        "Sometimes the issue is not processing. Tight editing, phase alignment, or better timing can make transients feel stronger and cleaner.",
    },
  ];

  const problems = [
    {
      title: "Too sharp",
      description:
        "The transient jumps out and feels pokey, clicky, brittle, or distracting. Try softer attack shaping, careful compression, EQ, or saturation.",
    },
    {
      title: "Too dull",
      description:
        "The sound lacks definition at the front. It may need more attack, better EQ focus, less compression, or a clearer performance/source tone.",
    },
    {
      title: "Too long",
      description:
        "The sustain or room tone covers the next hit. Shortening sustain can make drums, guitars, and percussion feel tighter.",
    },
    {
      title: "Too flat",
      description:
        "Over-compression, heavy limiting, or excessive clipping can remove the movement between attack and body, making the track feel smaller.",
    },
  ];

  const mistakes = [
    "Boosting attack on every drum track and making the kit feel clicky instead of punchy.",
    "Using compression with a very fast attack when you actually want more transient impact.",
    "Clipping too hard before understanding whether the transient is helping or hurting the mix.",
    "Judging transients only in solo instead of listening to how the part cuts through the full track.",
    "Removing too much sustain and making drums or instruments feel disconnected from the space.",
    "Trying to add punch with EQ when the real problem is timing, phase, compression, or envelope shape.",
  ];

  const workflowTips = [
    {
      title: "Decide what the transient is supposed to do",
      description:
        "A snare may need to crack, a kick may need to punch, and a bass may only need enough attack to speak clearly. Do not process every transient the same way.",
    },
    {
      title: "Separate attack from sustain in your mind",
      description:
        "When a sound feels wrong, ask whether the beginning is the problem or the tail is the problem. That makes choosing the right tool much easier.",
    },
    {
      title: "Use slow compressor attack for more punch",
      description:
        "If a compressor clamps down too quickly, it can remove the initial impact. A slower attack can let the transient through before compression grabs the body.",
    },
    {
      title: "Use transient shaping for direct control",
      description:
        "When you want more attack or less sustain without dialing in threshold, ratio, attack, and release, a transient shaper can be faster.",
    },
    {
      title: "Check the groove",
      description:
        "Transients define rhythm. If they are too soft, the groove can feel lazy. If they are too sharp, the groove can feel stiff or uncomfortable.",
    },
  ];

  const relatedLinks = [
    { label: "Transient Shaper", to: "/learning/transient-shaper" },
    { label: "How to Get Punchy Drums", to: "/learning/how-to-get-punchy-drums" },
    { label: "Compression", to: "/learning/compression" },
    { label: "Clipping vs Limiting", to: "/learning/clipping-vs-limiting" },
    { label: "Saturation vs Distortion vs Clipping", to: "/learning/saturation-vs-distortion-vs-clipping" },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const innerPanelSx = {
    borderRadius: "16px",
    backgroundColor: style.darkPanel,
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "none",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="What Is a Transient in Audio? | Parish Audio Learning"
        description="Learn what transients are in audio, how attack and sustain shape punch, clarity, rhythm, and impact, and how to control transients in mixing."
        canonical="https://parishaudio.com/learning/what-is-a-transient"
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        imageAlt="Recording studio background for a Parish Audio article about audio transients"
      />

      <ArticleSchemaMarkup
        canonical="https://parishaudio.com/learning/what-is-a-transient"
        headline="What Is a Transient in Audio?"
        description="Learn what transients are in audio, how attack and sustain shape punch, clarity, rhythm, and impact, and how to control transients in mixing."
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        articleSection="Audio Education"
        datePublished="2026-05-26T00:00:00-06:00"
        dateModified="2026-05-26T00:00:00-06:00"
        keywords={[
          "what is a transient",
          "audio transient",
          "transients in audio",
          "transient shaper",
          "attack and sustain",
          "drum transients",
          "mixing transients",
          "music production",
          "audio attack",
          "punchy drums",
        ]}
        breadcrumbs={[
          { name: "Home", item: "https://parishaudio.com/" },
          { name: "Learning", item: "https://parishaudio.com/learning" },
          {
            name: "What Is a Transient in Audio?",
            item: "https://parishaudio.com/learning/what-is-a-transient",
          },
        ]}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 280, md: 340 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.86) 0%, rgba(8,12,18,0.75) 38%, rgba(8,12,18,0.46) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 820 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Mixing Guide"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.15rem", md: "3.4rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      What Is a Transient in Audio?
                    </Typography>

                    <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
                      May 26, 2026
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 760,
                      }}
                    >
                      A transient is the fast burst of energy at the beginning of a sound.
                      It is a major part of attack, punch, rhythm, clarity, and impact. If
                      you have ever made drums hit harder, softened a harsh pick attack, or
                      tried to make bass notes speak more clearly, you were working with
                      transients.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: style.color3,
                            lineHeight: 1.55,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MenuBookRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      What a Transient Actually Is
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    In audio, a transient is a short, high-energy moment that happens when
                    a sound begins or changes suddenly. The most obvious examples are drum
                    hits: the beater hitting a kick drum, the stick striking a snare, or
                    the attack of a hi-hat. But transients exist in many sounds, including
                    vocals, guitars, piano, bass, percussion, and synths.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    The transient is not always the loudest part of the sound, but it is
                    often the part that tells your ear what happened. It helps define the
                    timing of the performance, the front edge of the note, and the sense of
                    impact. Without clear transients, a mix can feel soft, blurred, or
                    pushed backward.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Transients are also a major reason two sounds with similar frequency
                    content can feel completely different. A picked bass note, a fingered
                    bass note, and a synth bass may occupy similar low-frequency space, but
                    their attacks make them behave differently in the mix.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WavesRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Attack, Body, Sustain, and Decay
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    It helps to think of a sound in sections. These sections are not always
                    perfectly separate, but they make it easier to understand what you are
                    hearing and what kind of processing might help.
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                      gap: 1.25,
                    }}
                  >
                    {partsOfSound.map((item) => (
                      <Paper key={item.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Stack spacing={0.8}>
                          <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                            {item.title}
                          </Typography>

                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                            {item.description}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    A transient shaper usually focuses on the relationship between attack
                    and sustain. Increasing attack can make the front of a sound hit harder.
                    Reducing sustain can make the sound tighter. Increasing sustain can
                    make room tone, ring, or decay more obvious.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <HeadphonesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Why Transients Matter in a Mix
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Transients help a mix feel clear and rhythmically defined. They tell
                      the ear where hits begin, where notes start, and how different parts
                      lock together. A kick with a clear transient can feel tight even when
                      it has plenty of low end. A snare with the right attack can cut
                      through without being overly loud.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Transients also help separation. If several instruments have blurred
                      or overly compressed attacks, they can smear together even when their
                      EQ balance is reasonable. Clear attack information lets the ear
                      identify each part more easily.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CompressRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Transients and Headroom
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Transients can create high peaks without adding much perceived
                      loudness. That is why a snare, kick, or plucked bass can hit the
                      meter hard while still not feeling loud enough in the mix.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      This is one reason clipping, compression, limiting, and transient
                      shaping are important. They help control the relationship between
                      short peaks and average loudness. The goal is not always to remove
                      transients. The goal is to decide which transients help the mix and
                      which ones are wasting headroom.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LibraryMusicRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Common Examples of Transients
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Transients are easiest to hear on drums, but they show up in almost
                    every source. Once you start listening for the front edge of a sound,
                    you can make better decisions about compression, EQ, clipping, editing,
                    and transient shaping.
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                      gap: 1.25,
                    }}
                  >
                    {transientExamples.map((item) => (
                      <Paper key={item.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Stack spacing={0.8}>
                          <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                            {item.title}
                          </Typography>

                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                            {item.description}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TuneRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Tools That Shape Transients
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    There are several ways to control transients. Some are direct, like a
                    transient shaper. Others affect transients indirectly by changing level,
                    frequency balance, harmonics, or peak shape.
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                      gap: 1.25,
                    }}
                  >
                    {transientControls.map((item) => (
                      <Paper key={item.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Stack spacing={0.8}>
                          <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                            {item.title}
                          </Typography>

                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                            {item.description}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Common Transient Problems
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {problems.map((item) => (
                        <Paper
                          key={item.title}
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                          }}
                        >
                          <Stack spacing={0.6}>
                            <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                              {item.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: style.color3, lineHeight: 1.65 }}
                            >
                              {item.description}
                            </Typography>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Common Mistakes
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.25, lineHeight: 1.75 },
                      }}
                    >
                      {mistakes.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TuneRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Practical Workflow: How to Control Transients
                    </Typography>
                  </Stack>

                  <Stack spacing={1.2}>
                    {workflowTips.map((step, index) => (
                      <Paper
                        key={step.title}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Stack spacing={0.6}>
                          <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                            {index + 1}. {step.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: style.color3, lineHeight: 1.65 }}
                          >
                            {step.description}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Example: Making Drums Hit Harder
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      If a drum loop feels flat, the problem may not be volume. The attack
                      may be too soft, the sustain may be too long, or the peaks may be
                      eating headroom without creating useful punch.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      A transient shaper can increase attack so the drums feel more
                      immediate, reduce sustain so the groove tightens up, or add punch
                      while keeping the workflow fast. This is the kind of problem Striker
                      is designed to handle: shaping attack, sustain, punch, and impact
                      without making the process complicated.
                    </Typography>

                    <Button
                      component={RouterLink}
                      to="/products/striker"
                      sx={{
                        alignSelf: "flex-start",
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: style.accentGreen,
                        "&:hover": {
                          backgroundColor: style.accentGreen,
                          filter: "brightness(1.05)",
                        },
                      }}
                    >
                      View Striker
                    </Button>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MenuBookRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Related Topics
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                      These topics connect directly to transients, attack, sustain, punch,
                      compression, clipping, and drum impact.
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {relatedLinks.map((link) => (
                        <Button
                          key={link.to}
                          component={RouterLink}
                          to={link.to}
                          sx={{
                            borderRadius: "5px",
                            textTransform: "none",
                            fontWeight: 700,
                            color: style.color4,
                            backgroundColor: "rgba(255,255,255,0.05)",
                            border: `1px solid ${accent}`,
                            "&:hover": {
                              borderColor: style.color4,
                              backgroundColor: "rgba(255,255,255,0.10)",
                            },
                          }}
                        >
                          {link.label}
                        </Button>
                      ))}
                    </Stack>

                    <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      Transients are easy to overlook because they happen quickly, but they
                      have a huge effect on how a mix feels. Control them well and tracks
                      can sound tighter, punchier, clearer, and more intentional without
                      simply turning everything up.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>
            </Box>
          </Stack>
        </Box>
      </Box>
    </>
  );
}